import React, { useEffect, useState } from "react";
import { Bot, Check } from "lucide-react";
import { Modal, VbButton } from "@/components/vb";
import { SUGGEST_TARGETS, type SuggestTarget } from "./useBuyerWizard";

const LABELS: Record<SuggestTarget, { label: string; hint: string }> = {
  name: { label: "Name", hint: "What you'll call this buyer" },
  description: { label: "Description", hint: "Who they are, what they care about" },
  region: { label: "Market region", hint: "Where they're based" },
  competitors: { label: "Competitors", hint: "Who AI compares you against" },
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which fields already have a value — used to default the checkboxes to the
   *  empty ones, so the common case (fill the gaps) is one click. */
  filled: Record<SuggestTarget, boolean>;
  onConfirm: (targets: SuggestTarget[]) => void;
};

/** "What should Marcus suggest?" — shown when the user clicks Suggest with
 *  something already filled in, so a suggestion never silently overwrites their
 *  effort. Pre-checks the empty fields; whatever's checked gets overwritten,
 *  everything else is kept exactly as typed. */
export function SuggestScopeDialog({ open, onOpenChange, filled, onConfirm }: Props) {
  const empties = SUGGEST_TARGETS.filter((t) => !filled[t]);
  const [selected, setSelected] = useState<SuggestTarget[]>(empties);

  // Re-seed the defaults each time the dialog opens (the form may have changed
  // since the last open). Keyed on `open` so re-opening resets cleanly.
  useEffect(() => {
    if (open) setSelected(SUGGEST_TARGETS.filter((t) => !filled[t]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (t: SuggestTarget) =>
    setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="What should Marcus suggest?"
      description="Pick the fields to fill. Everything you leave unchecked stays exactly as you wrote it."
      footer={
        <>
          <VbButton variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </VbButton>
          <VbButton
            disabled={selected.length === 0}
            onClick={() => {
              onConfirm(selected);
              onOpenChange(false);
            }}
          >
            <Bot size={15} aria-hidden="true" />
            Suggest {selected.length > 0 ? `(${selected.length})` : ""}
          </VbButton>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        {SUGGEST_TARGETS.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              aria-pressed={on}
              className={[
                "flex items-center gap-3 rounded-vb-md border px-3 py-2.5 text-left transition-colors",
                on
                  ? "border-vb-brand bg-vb-brand-soft"
                  : "border-vb-line bg-vb-surface hover:bg-vb-bg-subtle",
              ].join(" ")}
            >
              <span
                className={[
                  "grid h-5 w-5 shrink-0 place-items-center rounded-vb-xs border",
                  on
                    ? "border-vb-brand bg-vb-brand text-vb-on-brand"
                    : "border-vb-line-strong bg-vb-surface text-transparent",
                ].join(" ")}
              >
                <Check size={13} aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-[13.5px] font-bold text-vb-text">
                  {LABELS[t].label}
                  {!filled[t] && (
                    <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-vb-text-3">
                      empty
                    </span>
                  )}
                </span>
                <span className="text-[12px] text-vb-text-2">{LABELS[t].hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

export default SuggestScopeDialog;
